import { LucideIcon } from 'lucide-react'

import { cn } from '@rewardops/ui'

import { Card, CardContent } from './ui/card'

interface MetricCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon?: LucideIcon | null
  trend?: {
    value: number
    isPositive: boolean
    label?: string
  }
  variant?: 'earnings' | 'redemptions' | 'available' | 'default'
  className?: string
  allowPrefix?: boolean
}

export default function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  variant = 'default',
  className,
  allowPrefix = true,
}: MetricCardProps) {
  const gradientClass = {
    earnings: 'bg-[var(--gradient-earnings)]',
    redemptions: 'bg-[var(--gradient-redemptions)]',
    available: 'bg-[var(--gradient-available)]',
    default: '',
  }[variant]

  const getValuePrefix = () => {
    const normalizedTitle = title?.toLowerCase()
    if (!allowPrefix) return ''
    return normalizedTitle?.startsWith('rev')
      ? '$'
      : normalizedTitle?.includes('points')
      ? '+'
      : ''
  }

  return (
    <Card
      className={cn('overflow-visible', gradientClass, className)}
      data-testid={`card-metric-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="flex min-h-[180px] flex-col items-center justify-center p-6">
        <div className="mb-4 rounded-md bg-primary/10 p-3">
          {Icon ? <Icon className="h-6 w-6 text-tenant-primary-color" /> : null}
        </div>
        <div className="w-full text-center">
          <p
            className="mb-3 text-sm font-medium text-muted-foreground"
            data-testid={`text-metric-title-${title
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
          >
            {title}
          </p>
          <div
            className="text-2xl font-bold"
            data-testid={`text-metric-value-${title
              .toLowerCase()
              .replace(/\s+/g, '-')}`}
          >
            {getValuePrefix()}
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          {subtitle && (
            <p
              className="mt-2 text-sm text-muted-foreground"
              data-testid={`text-metric-subtitle-${title
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              {subtitle}
            </p>
          )}
          {trend && (
            <div className="mt-3 flex items-center justify-center gap-1">
              <span
                className={cn(
                  'text-sm font-medium',
                  trend.isPositive ? 'text-success' : 'text-destructive',
                )}
                data-testid={`text-metric-trend-${title
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`}
              >
                {trend.isPositive ? '+' : ''}
                {trend.value}%
              </span>
              <span className="text-xs text-muted-foreground">
                {trend.label || 'vs last month'}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
